import React, { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import USE_CASES_SEED from '../data/useCasesSeed';
import { useUseCases } from '../hooks/useEntityQueries';
import { QUERY_KEYS } from '../queryClient';

const DOMAINS = [
  { id: 'fincrime', label: 'Financial Crime', icon: '🔍', color: '#0369a1' },
  { id: 'custint', label: 'Customer Intelligence', icon: '👥', color: '#7c3aed' },
  { id: 'risk', label: 'Risk Management', icon: '⚠️', color: '#d97706' },
  { id: 'supply', label: 'Supply Chain', icon: '🔗', color: '#e11d48' },
  { id: 'datamgmt', label: 'Data Management', icon: '🗄️', color: '#0d9488' },
  { id: 'esg', label: 'ESG & Climate', icon: '🌱', color: '#059669' },
  { id: 'suptech', label: 'Regulatory & SupTech', icon: '🏦', color: '#b45309' },
  { id: 'customs', label: 'Customs & Border', icon: '🚢', color: '#c2410c' },
  { id: 'grants', label: 'Grants & Benefits', icon: '🏛️', color: '#059669' },
  { id: 'tax', label: 'Tax & Revenue', icon: '💰', color: '#0369a1' },
  { id: 'law', label: 'Law Enforcement', icon: '⚖️', color: '#3f6212' },
];
const MATURITY = [
  { id: 'live', label: 'Live', color: '#059669', bg: '#ecfdf5' },
  { id: 'prototype', label: 'Prototype', color: '#0369a1', bg: '#eff6ff' },
  { id: 'mvp', label: 'MVP Ready', color: '#7c3aed', bg: '#f5f3ff' },
  { id: 'development', label: 'In Development', color: '#b45309', bg: '#fffbeb' },
  { id: 'planned', label: 'Planned', color: '#6b7280', bg: '#f9fafb' },
  { id: 'concept', label: 'Concept', color: '#9ca3af', bg: '#f3f4f6' },
];
const IMPORTANCE = [
  { id: 'critical', label: 'Critical', color: '#dc2626', icon: '🔴' },
  { id: 'high', label: 'High', color: '#ea580c', icon: '🟠' },
  { id: 'medium', label: 'Medium', color: '#ca8a04', icon: '🟡' },
  { id: 'low', label: 'Low', color: '#6b7280', icon: '⚪' },
];
const DEMAND = [
  { id: 'proven', label: 'Proven Demand', color: '#059669', icon: '🔥' },
  { id: 'strong', label: 'Strong Signal', color: '#0369a1', icon: '📈' },
  { id: 'emerging', label: 'Emerging', color: '#b45309', icon: '🌱' },
  { id: 'exploratory', label: 'Exploratory', color: '#6b7280', icon: '🔬' },
];

function getLookup(arr, id) { return arr.find(x => x.id === id) || arr[0]; }

function UseCaseManager() {
  const queryClient = useQueryClient();
  const { data: rawData, isLoading, isError } = useUseCases();
  const [editModal, setEditModal] = useState(null);
  const [search, setSearch] = useState('');

  const norm = (u) => ({
    id: u.Id || u.id,
    ucId: u.UCId || u.ucId || u.id,
    name: u.Name || u.name || '',
    domain: u.Domain || u.domain || '',
    phase: u.Phase || u.phase || 3,
    maturity: u.Maturity || u.maturity || 'concept',
    importance: u.Importance || u.importance || 'medium',
    demand: u.Demand || u.demand || 'emerging',
    competitors: u.Competitors || u.competitors || '',
    personas: u.Personas || u.personas || '',
    notes: u.Notes || u.notes || '',
    painPoints: u.PainPoints || u.painPoints || '',
    regionalSoftware: u.RegionalSoftware || u.regionalSoftware || '',
    competitorIssues: u.CompetitorIssues || u.competitorIssues || '',
    positioning: u.Positioning || u.positioning || '',
    pitchDeckUrl: u.PitchDeckUrl || u.pitchDeckUrl || '',
    demoUrl: u.DemoUrl || u.demoUrl || '',
  });

  const data = useMemo(() => {
    if (isError) return USE_CASES_SEED;
    return (rawData || []).map(norm);
  }, [rawData, isError]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.useCases });

  const deleteUseCase = async (id) => {
    if (window.confirm('Delete this use case?')) {
      try {
        await api.deleteUseCase(id);
        refresh();
      } catch (err) { console.error('Delete failed:', err); }
    }
  };

  const addUseCase = async () => {
    try {
      const created = await api.createUseCase({ name: 'New Use Case', domain: 'fincrime', phase: 3, maturity: 'concept', importance: 'medium', demand: 'emerging' });
      refresh();
      setEditModal(norm(created));
    } catch (err) { console.error('Create failed:', err); }
  };

  const saveEdit = async (updated) => {
    try {
      await api.updateUseCase(updated.id, updated);
      refresh();
      setEditModal(null);
    } catch (err) { console.error('Save failed:', err); }
  };
  const [fDomain, setFDomain] = useState('');
  const [fMaturity, setFMaturity] = useState('');
  const [fImportance, setFImportance] = useState('');
  const [fDemand, setFDemand] = useState('');
  const [openDetails, setOpenDetails] = useState({});
  const [statFilter, setStatFilter] = useState('');

  const filtered = useMemo(() => {
    return data.filter(u => {
      if (fDomain && u.domain !== fDomain) return false;
      if (fMaturity && u.maturity !== fMaturity) return false;
      if (fImportance && u.importance !== fImportance) return false;
      if (fDemand && u.demand !== fDemand) return false;
      if (statFilter === 'live' && u.maturity !== 'live') return false;
      if (statFilter === 'critical' && u.importance !== 'critical') return false;
      if (statFilter === 'proven' && u.demand !== 'proven') return false;
      if (search) {
        const s = search.toLowerCase();
        return (u.name.toLowerCase().includes(s) || (u.competitors || '').toLowerCase().includes(s) ||
          (u.painPoints || '').toLowerCase().includes(s) || (u.positioning || '').toLowerCase().includes(s));
      }
      return true;
    });
  }, [data, search, fDomain, fMaturity, fImportance, fDemand, statFilter]);

  const toggleDetail = (id) => setOpenDetails(prev => ({ ...prev, [id]: !prev[id] }));

  const clearFilters = () => { setSearch(''); setFDomain(''); setFMaturity(''); setFImportance(''); setFDemand(''); setStatFilter(''); };

  const domains = new Set(data.map(u => u.domain));
  const live = data.filter(u => u.maturity === 'live').length;
  const critical = data.filter(u => u.importance === 'critical').length;
  const proven = data.filter(u => u.demand === 'proven').length;

  const domainCounts = {};
  data.forEach(u => { domainCounts[u.domain] = (domainCounts[u.domain] || 0) + 1; });

  const badge = (label, color, bg) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, color, background: bg || color + '12', border: `1px solid ${color}25`, whiteSpace: 'nowrap' }}>{label}</span>
  );

  const statCard = (label, value, icon, bg, active, onClick) => (
    <div key={label} onClick={onClick} style={{ background: active ? '#eff6ff' : '#fff', border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 140px', minWidth: 140, boxShadow: '0 1px 4px rgba(0,0,0,.04)', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, background: bg }}>{icon}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );

  const formatContent = (text) => {
    if (!text) return 'No data yet.';
    return text.split('\\n').join('\n').split('\n').map((line, i) => <div key={i} style={{ marginBottom: 2 }}>{line}</div>);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: '#94a3b8', fontSize: 13 }}>
        Loading use cases…
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800 }}>Use Case Manager</h2>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>ContexQ · {data.length} use cases · {domains.size} domains</div>
        </div>
        <button onClick={addUseCase} style={{ background: '#0369a1', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>+ Add Use Case</button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {statCard('Total Use Cases', data.length, '📦', '#f0f9ff', statFilter === '', () => setStatFilter(''))}
        {statCard('Live', live, '⚡', '#ecfdf5', statFilter === 'live', () => setStatFilter(statFilter === 'live' ? '' : 'live'))}
        {statCard('Critical', critical, '🔴', '#fef2f2', statFilter === 'critical', () => setStatFilter(statFilter === 'critical' ? '' : 'critical'))}
        {statCard('Proven Demand', proven, '🔥', '#fff7ed', statFilter === 'proven', () => setStatFilter(statFilter === 'proven' ? '' : 'proven'))}
        {statCard('Domains', domains.size, '🗂️', '#f5f3ff', false, () => {})}
      </div>

      {/* Domain Distribution Bar */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 20px', marginBottom: 18, boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10, fontFamily: 'monospace' }}>Domain Distribution</div>
        <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', height: 28, gap: 2 }}>
          {DOMAINS.filter(d => domainCounts[d.id]).map(d => {
            const count = domainCounts[d.id] || 0;
            return (
              <div key={d.id} onClick={() => setFDomain(fDomain === d.id ? '' : d.id)}
                style={{ flex: count, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, cursor: 'pointer', background: fDomain === d.id ? d.color : d.color + '30', color: fDomain === d.id ? '#fff' : d.color, minWidth: 24, transition: 'all .2s' }}>
                {count}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
          {DOMAINS.filter(d => domainCounts[d.id]).map(d => (
            <span key={d.id} onClick={() => setFDomain(fDomain === d.id ? '' : d.id)}
              style={{ fontSize: 10, fontWeight: 600, cursor: 'pointer', color: fDomain === d.id ? d.color : '#6b7280' }}>
              {d.icon} {d.label} ({domainCounts[d.id]})
            </span>
          ))}
        </div>
      </div>

      {/* Search + Filters */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 16px', marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 280px', display: 'flex', alignItems: 'center', gap: 8, background: '#f4f5f9', borderRadius: 8, padding: '0 12px' }}>
            <span style={{ color: '#9ca3af' }}>🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search use cases, competitors..." style={{ border: 'none', background: 'none', padding: '9px 0', fontSize: 13, outline: 'none', width: '100%' }} />
          </div>
          <select value={fMaturity} onChange={e => setFMaturity(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            <option value="">All Maturity</option>
            {MATURITY.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <select value={fImportance} onChange={e => setFImportance(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            <option value="">All Importance</option>
            {IMPORTANCE.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <select value={fDemand} onChange={e => setFDemand(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
            <option value="">All Demand</option>
            {DEMAND.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {(search || fDomain || fMaturity || fImportance || fDemand || statFilter) && (
            <button onClick={clearFilters} style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#fff', cursor: 'pointer' }}>Clear</button>
          )}
        </div>
      </div>

      {/* Results count */}
      <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>Showing {filtered.length} of {data.length} use cases</div>

      {/* Use Case List */}
      {filtered.map(uc => {
        const dom = getLookup(DOMAINS, uc.domain);
        const mat = getLookup(MATURITY, uc.maturity);
        const imp = getLookup(IMPORTANCE, uc.importance);
        const dem = getLookup(DEMAND, uc.demand);
        const isOpen = openDetails[uc.id];

        return (
          <div key={uc.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 8, boxShadow: '0 1px 4px rgba(0,0,0,.04)', overflow: 'hidden' }}>
            {/* Row header */}
            <div onClick={() => toggleDetail(uc.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14, flex: '1 1 300px' }}>
                {isOpen ? '▼' : '▶'} {uc.name}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {badge(dom.icon + ' ' + dom.label, dom.color)}
                {badge('P' + uc.phase, '#6b7280', '#f3f4f6')}
                {badge(mat.label, mat.color, mat.bg)}
                <span style={{ fontSize: 12, fontWeight: 600, color: imp.color }}>{imp.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: dem.color }}>{dem.icon}</span>
                <button onClick={(e) => { e.stopPropagation(); setEditModal({ ...uc }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 4px' }} title="Edit">✏️</button>
                <button onClick={(e) => { e.stopPropagation(); deleteUseCase(uc.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 4px' }} title="Delete">🗑️</button>
              </div>
            </div>

            {/* Expanded detail */}
            {isOpen && (
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f3f4f6' }}>
                {/* Meta info */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '12px 0', fontSize: 12, color: '#6b7280' }}>
                  <div><strong>Competitors:</strong> {uc.competitors || '—'}</div>
                  <div><strong>Personas:</strong> {uc.personas || '—'}</div>
                </div>

                {/* 4 detail sections */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
                  {[
                    ['🎯 Customer Pain Points', uc.painPoints],
                    ['🌏 Regional Software (ASEAN / MENA / EU)', uc.regionalSoftware],
                    ['⚠️ Issues with Existing Solutions', uc.competitorIssues],
                    ['🏆 ContexQ Competitive Positioning', uc.positioning],
                  ].map(([label, content]) => (
                    <div key={label} style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>{label}</div>
                      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {formatContent(content)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Links */}
                {(uc.pitchDeckUrl || uc.demoUrl) && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                    {uc.pitchDeckUrl && <a href={uc.pitchDeckUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#2563eb' }}>📄 Pitch Deck</a>}
                    {uc.demoUrl && <a href={uc.demoUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#2563eb' }}>🖥️ Demo</a>}
                  </div>
                )}

                {uc.notes && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>Notes: {uc.notes}</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Edit Modal */}
      {editModal && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setEditModal(null); }} style={{ display: 'flex', position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, alignItems: 'flex-start', justifyContent: 'center', padding: '2rem', overflowY: 'auto', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 18, maxWidth: 700, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.12)', position: 'relative', padding: '1.5rem' }}>
            <button onClick={() => setEditModal(null)} style={{ position: 'absolute', top: '.8rem', right: '.8rem', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 7, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>✕</button>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 16 }}>Edit Use Case</h2>
            {[
              ['Name', 'name', 'text'],
              ['Domain', 'domain', 'select', DOMAINS.map(d => ({ value: d.id, label: d.label }))],
              ['Phase', 'phase', 'select', [1,2,3,4,5,6,7].map(p => ({ value: p, label: 'Phase ' + p }))],
              ['Maturity', 'maturity', 'select', MATURITY.map(m => ({ value: m.id, label: m.label }))],
              ['Importance', 'importance', 'select', IMPORTANCE.map(m => ({ value: m.id, label: m.label }))],
              ['Demand', 'demand', 'select', DEMAND.map(m => ({ value: m.id, label: m.label }))],
              ['Competitors', 'competitors', 'text'],
              ['Personas', 'personas', 'text'],
              ['Pain Points', 'painPoints', 'textarea'],
              ['Regional Software', 'regionalSoftware', 'textarea'],
              ['Competitor Issues', 'competitorIssues', 'textarea'],
              ['Positioning', 'positioning', 'textarea'],
              ['Pitch Deck URL', 'pitchDeckUrl', 'text'],
              ['Demo URL', 'demoUrl', 'text'],
              ['Notes', 'notes', 'text'],
            ].map(([label, field, type, options]) => (
              <div key={field} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 3 }}>{label}</label>
                {type === 'select' ? (
                  <select value={editModal[field] || ''} onChange={e => setEditModal(prev => ({ ...prev, [field]: field === 'phase' ? parseInt(e.target.value) : e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none' }}>
                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : type === 'textarea' ? (
                  <textarea value={(editModal[field] || '').replace(/\\n/g, '\n')} onChange={e => setEditModal(prev => ({ ...prev, [field]: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none', minHeight: 80, resize: 'vertical' }} />
                ) : (
                  <input type="text" value={editModal[field] || ''} onChange={e => setEditModal(prev => ({ ...prev, [field]: e.target.value }))} style={{ width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none' }} />
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setEditModal(null)} style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => saveEdit(editModal)} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#0369a1', color: '#fff', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UseCaseManager;
